import { mdiCheck, mdiClose } from '@mdi/js';
import type { MappedCustomDataField, SelectValue } from 'common/models';
import Icon from 'components/Icon/Icon';
import { toLocaleString } from 'helpers/mathHelpers';
import { renderDate, renderLegacyDateTime, renderTime } from 'helpers/timeHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';

import LabeledText from '../LabeledText/LabeledText';
import CustomDataCheckList from './CustomDataCheckList';
import CustomDataGeoCoordinate from './CustomDataGeoCoordinate';
import VatSelectInfo from './VatSelectInfo';

interface Props {
  customData: MappedCustomDataField;
  totalCost?: number;
  currencyId?: string;
}

const CustomDataLabeledText = (props: Props) => {
  const { customData, totalCost, currencyId } = props;

  const { i18n } = useCustomTranslation();

  switch (customData.formField.type) {
    case 'DateTime':
      return (
        <LabeledText
          label={customData.label}
          value={
            customData.value &&
            renderLegacyDateTime(customData.value, customData.format || 'LLL', i18n.language)
          }
        />
      );
    case 'Date':
      return (
        <LabeledText
          label={customData.label}
          value={
            customData.value &&
            renderDate(customData.value, customData.format || 'LL', i18n.language)
          }
        />
      );
    case 'Time':
      return (
        <LabeledText
          label={customData.label}
          value={
            customData.value &&
            renderTime(customData.value, customData.format || 'LT', i18n.language)
          }
        />
      );
    case 'Boolean':
      return (
        <LabeledText
          label={customData.label}
          value={
            customData.value ? (
              <Icon path={mdiCheck} className="text-success" />
            ) : (
              <Icon path={mdiClose} className="text-danger" />
            )
          }
        />
      );
    case 'Number': {
      const value =
        customData.value || customData.value === 0
          ? toLocaleString(customData.value * (customData?.multiplier || 1))
          : '';

      return (
        <LabeledText
          label={customData.label}
          value={customData.formField.unit ? `${value} ${customData.formField.unit}` : value}
        />
      );
    }
    case 'Select':
      if (customData.formField.isMultiSelect) {
        return (
          <LabeledText
            label={customData.label}
            value={customData.value?.map((value: SelectValue) => value.stringValue).join(', ')}
          />
        );
      }

      return <LabeledText label={customData.label} value={customData.stringValue} />;
    case 'VatSelect':
      return (
        <VatSelectInfo
          customData={customData}
          vatBase={totalCost || 0}
          currencyId={currencyId || ''}
        />
      );
    case 'PaymentType':
      return <LabeledText label={customData.label} value={customData.value.stringValue} />;
    case 'CheckList':
      return (
        <CustomDataCheckList
          data={customData.value}
          title={customData.label}
          checkListProps={{
            falseLabel: customData.formField.falseLabel,
            itemLabel: customData.formField.itemLabel,
            options: customData.formField.options,
            requireDescriptionWhenFalse: customData.formField.requireDescriptionWhenFalse,
            requireDescriptionWhenTrue: customData.formField.requireDescriptionWhenTrue,
            showIndex: customData.formField.showIndex,
            trueLabel: customData.formField.trueLabel,
            descriptionLabel: customData.formField.descriptionLabel
          }}
        />
      );
    case 'GeoCoordinate':
      return <CustomDataGeoCoordinate label={customData.label} location={customData.value} />;
    case 'Text':
      if (customData.formField.isMultiLine) {
        return (
          <LabeledText
            label={customData.label}
            value={<div className="MultiLineText">{customData.value}</div>}
          />
        );
      }
      return <LabeledText label={customData.label} value={customData.value} />;
    default:
      return <LabeledText label={i18n.t('customData.unssuportedField')} />;
  }
};

export default CustomDataLabeledText;
